import React from "react";
import { Scale, BookOpen, Landmark, Globe2, Gavel, Building2 } from "lucide-react";

/**
 * TrustedByStrip — grayscale credibility row positioned right below the hero.
 *
 * Shows the official data sources Dikigoros.io is grounded in, plus a few
 * professional-context placeholders for legal community. Designed to be
 * scannable in <2 seconds and convey "this is the real thing".
 *
 * Pure presentational (no network). Items hover to full color.
 */
export default function TrustedByStrip({ lang = "el" }) {
  const heading = lang === "el"
    ? "Δεδομένα από επίσημες πηγές · ενδεικτικές νομικές βάσεις"
    : "Data sourced from official Cyprus + EU legal databases";

  const items = [
    { Icon: Landmark, label: "Cyprus Bar Association", sub: lang === "el" ? "Παγκύπριος Δικ. Σύλλογος" : "Pancyprian Bar" },
    { Icon: BookOpen, label: "Cylaw.org",              sub: lang === "el" ? "Νομοθεσία & νομολογία" : "Legislation & case law" },
    { Icon: Gavel,    label: "Cylii",                  sub: lang === "el" ? "Πρωτογενής νομολογία" : "Primary case law" },
    { Icon: Globe2,   label: "ECHR · HUDOC",            sub: lang === "el" ? "Ευρωπαϊκό Δικαστήριο" : "European Court" },
    { Icon: Scale,    label: "Cyprus Constitution",     sub: lang === "el" ? "Σύνταγμα Κύπρου" : "Cyprus Constitution" },
    { Icon: Building2, label: "EU Law (EUR-Lex)",       sub: lang === "el" ? "Δίκαιο της ΕΕ" : "EU legislation" },
  ];

  return (
    <section
      aria-label={heading}
      className="border-y border-slate-200 bg-gradient-to-b from-white to-slate-50/40"
      data-testid="trusted-by-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FBE9EE] text-[#8B1E3F] text-[10px] font-bold tracking-[0.18em] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8B1E3F]" />
            {lang === "el" ? "Αξιόπιστες πηγές" : "Trusted sources"}
          </div>
          <p className="mt-3 text-sm text-slate-600">{heading}</p>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {items.map(({ Icon, label, sub }) => (
            <div
              key={label}
              className="group rounded-xl border border-slate-200 bg-white px-4 py-4 transition hover:border-[#8B1E3F]/40 hover:shadow-[0_8px_20px_rgba(139,30,63,0.10)] hover:-translate-y-0.5 flex flex-col items-center text-center"
              data-testid={`trusted-by-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <div className="h-9 w-9 rounded-lg bg-slate-100 group-hover:bg-[#FBE9EE] flex items-center justify-center transition">
                <Icon className="h-4 w-4 text-slate-500 group-hover:text-[#8B1E3F] transition" />
              </div>
              <div className="mt-2.5 font-display font-bold text-[13px] text-slate-700 group-hover:text-[#0A192F] transition leading-tight">
                {label}
              </div>
              <div className="text-[10.5px] text-slate-400 group-hover:text-slate-500 transition leading-tight mt-0.5">
                {sub}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
